import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MARKET_NAMES, type MarketCode } from '@hc/shared';
import { PanelShell } from '../panel-shell.js';
import { Badge } from '../badge.js';
import { useActiveCountry, type PanelId, type TabId } from '../store.js';
import { fetchDemandShifts, type DemandProjectionRow } from '../../api/client.js';

// Demand Shifts — the demand side of the buy-and-build equation, read from
// OFFICIAL population projections rather than news: Eurostat EUROPOP2023
// baseline for the EU/EFTA markets (national grain) and ONS 2022-based
// NPP/SNPP for GB (national + regions + devolved nations). This panel
// replaces the retired news-scraping demand-side agent.
//
// Reading guide: 80+ growth is the care-need proxy (residential elderly,
// domiciliary, nursing); 65+ drives audiology, ophthalmology and specialist
// medical volumes; total population drives dental, pharmacy and primary care.
// Growth renders in the direction palette (--positive / --negative).

const STALE_MS = 60 * 60_000; // projections change ~annually

type Band = 'total' | '65plus' | '80plus';
const BAND_LABEL: Record<Band, string> = { total: 'Total', '65plus': '65+', '80plus': '80+' };

interface CellKey {
  band: Band;
  horizon: number;
}
const COLUMNS: CellKey[] = [
  { band: 'total', horizon: 2035 },
  { band: '65plus', horizon: 2030 },
  { band: '65plus', horizon: 2035 },
  { band: '80plus', horizon: 2030 },
  { band: '80plus', horizon: 2035 },
];

function Pct({ v }: { v: number | null | undefined }) {
  if (v == null) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
  const color =
    v > 0
      ? 'var(--positive, #0a7d5a)'
      : v < 0
        ? 'var(--negative, #c0143c)'
        : 'var(--text-secondary)';
  return (
    <span style={{ color, fontVariantNumeric: 'tabular-nums' }}>
      {v > 0 ? '+' : ''}
      {v.toFixed(1)}%
    </span>
  );
}

function useDemandData() {
  return useQuery({
    queryKey: ['demand-shifts'] as const,
    queryFn: fetchDemandShifts,
    staleTime: STALE_MS,
  });
}

/** (scopeKey → band → horizon → pct); regional rows grouped per country.
 *  Until 2026-07-21 the regional branch was `else if (r.countryCode === 'GB')`
 *  — reasonable when GB/ONS was the only regional source, but it silently
 *  DISCARDED regional rows for anyone else. The Eurostat proj_25rp5 backfill
 *  now provides NUTS2 rows for 24 more markets, so grouping is generic.
 *  (CH and NO remain national-only: EUROPOP2025 covers EU-27, no EFTA.) */
function indexRows(rows: DemandProjectionRow[]) {
  const national = new Map<string, Map<string, number | null>>();
  const regionsByCountry = new Map<string, Map<string, Map<string, number | null>>>();
  for (const r of rows) {
    const cell = `${r.ageBand}:${r.horizonYear}`;
    if (r.region == null) {
      const m = national.get(r.countryCode) ?? new Map();
      m.set(cell, r.pctChange);
      national.set(r.countryCode, m);
    } else {
      const byRegion = regionsByCountry.get(r.countryCode) ?? new Map();
      const m = byRegion.get(r.region) ?? new Map();
      m.set(cell, r.pctChange);
      byRegion.set(r.region, m);
      regionsByCountry.set(r.countryCode, byRegion);
    }
  }
  return { national, regionsByCountry };
}

function GrowthTable({
  title,
  rows,
  nameOf,
}: {
  title: string;
  rows: Array<[string, Map<string, number | null>]>;
  nameOf: (key: string) => string;
}) {
  return (
    <div style={{ marginBottom: 'var(--s-3)' }}>
      <div className="section-header">{title}</div>
      <table className="table">
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }} />
            {COLUMNS.map((c) => (
              <th key={`${c.band}${c.horizon}`} className="num" title="Growth vs 2025 base">
                {BAND_LABEL[c.band]} →’{String(c.horizon).slice(2)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(([key, cells]) => (
            <tr key={key}>
              <td>
                <strong>{nameOf(key)}</strong>
              </td>
              {COLUMNS.map((c) => (
                <td key={`${c.band}${c.horizon}`} className="num">
                  <Pct v={cells.get(`${c.band}:${c.horizon}`)} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function DemandShiftsV2Panel({ tabId, panelId }: { tabId: TabId; panelId: PanelId }) {
  const { data, isLoading, isError } = useDemandData();
  const { national, regionsByCountry } = useMemo(() => indexRows(data?.data ?? []), [data]);
  const activeCountry = useActiveCountry();

  // Markets sorted by the care-need proxy (80+ growth to 2035), strongest first.
  const marketRows = useMemo(
    () =>
      [...national.entries()].sort(
        (a, b) => (b[1].get('80plus:2035') ?? -999) - (a[1].get('80plus:2035') ?? -999),
      ),
    [national],
  );
  // Regional detail follows the workspace's active country — every market with
  // regional rows gets the same drill GB always had (25 markets today).
  const regionRows = useMemo(() => {
    const regions = (activeCountry && regionsByCountry.get(activeCountry)) || new Map();
    return [...regions.entries()].sort(
      (a, b) => (b[1].get('80plus:2035') ?? -999) - (a[1].get('80plus:2035') ?? -999),
    );
  }, [regionsByCountry, activeCountry]);

  return (
    <PanelShell
      tabId={tabId}
      panelId={panelId}
      toolbar={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 'var(--s-2) var(--s-3)',
          }}
        >
          <div className="section-header" style={{ marginBottom: 0 }}>
            Population-projection demand outlook · base 2025
          </div>
          {isError ? (
            <Badge variant="alert">error</Badge>
          ) : isLoading ? (
            <Badge variant="neutral">loading…</Badge>
          ) : marketRows.length === 0 ? (
            <Badge variant="neutral">no data — run the demand ingest</Badge>
          ) : (
            <Badge variant="neutral">{marketRows.length} markets</Badge>
          )}
        </div>
      }
      loading={isLoading}
      error={isError ? 'Failed to load demand projections.' : false}
      empty={
        !isLoading && !isError && marketRows.length === 0
          ? 'No projections loaded — run scripts/ingest_demand_projections.mjs.'
          : false
      }
      padding="var(--s-3)"
    >
      <GrowthTable
        title="Markets — sorted by 80+ growth to 2035 (care-need proxy)"
        rows={marketRows}
        nameOf={(cc) => MARKET_NAMES[cc as MarketCode] ?? cc}
      />
      {regionRows.length > 0 && (
        <GrowthTable
          title={`${MARKET_NAMES[activeCountry as MarketCode] ?? activeCountry} — regional detail`}
          rows={regionRows}
          nameOf={(r) => r}
        />
      )}
      <div
        style={{
          fontSize: 'var(--t-meta)',
          color: 'var(--text-tertiary)',
          lineHeight: 1.5,
          marginTop: 'var(--s-2)',
        }}
      >
        80+ growth drives residential elderly, domiciliary and nursing demand; 65+ drives audiology,
        ophthalmology and specialist medical; total population drives dental, pharmacy and primary
        care. Sources: Eurostat EUROPOP2023 baseline projections (national) · ONS 2022-based
        national and subnational projections (GB; English regions + devolved nations). Official
        statistics, not news extraction.
      </div>
    </PanelShell>
  );
}
